import {
  FloatingArrow,
  Placement,
  arrow,
  autoPlacement,
  offset,
  useClientPoint,
  useDismiss,
  useFloating,
  useHover,
  useInteractions,
} from "@floating-ui/react";
import {
  TimeFormats,
  cn,
  createState,
  endOfDay,
  formatDate,
  parseDate,
  startOfDay,
  useProtectedActivityQuery,
} from "@theprelude/core";
import { CalendarIcon, useWindowSize } from "@theprelude/ds";
import React, { useEffect, useRef, useState } from "react";
import { shallow } from "zustand/shallow";
import DateSlider, { useDateRange } from "../date-slider";
import { LineChart, Point } from "./line-chart";

type CurrentPoints = {
  current: Point;
  benchmark: Point;
};

const useTooltipData = createState<CurrentPoints | null>(null);

export default function Timeline() {
  const range = useDateRange((state) => state.value, shallow);

  const chartRef = useRef<HTMLDivElement>(null);
  const windowSize = useWindowSize();
  const setTooltipData = useTooltipData((state) => state.setValue, shallow);
  const points = useProtectedActivityQuery(range, (data): Point[] => {
    return data.map((point) => ({
      date: parseDate(point.date),
      value: point.protected,
    }));
  });

  const socialPoints = useProtectedActivityQuery(
    {
      ...range,
      impersonate: "social",
    },
    (data): Point[] => {
      return data
        .filter((point) => point.protected !== null)
        .map((point) => ({
          date: parseDate(point.date),
          value: point.protected,
        }));
    },
  );
  useEffect(() => {
    if (!chartRef.current) {
      return;
    }

    const graph = new LineChart({
      data: points.data ?? [],
      benchmarkData: socialPoints.data ?? [],
      node: chartRef.current,
      start: startOfDay(parseDate(range.start), "Etc/UTC"),
      finish: endOfDay(parseDate(range.finish)),
      tooltip: {
        clear: () => {
          setTooltipData(null);
        },
        set: (point, benchmarkPoint) => {
          setTooltipData({
            current: point,
            benchmark: benchmarkPoint,
          });
        },
      },
    });

    return () => {
      graph.cleanup();
      setTooltipData(null);
    };
  }, [
    points.data,
    range.finish,
    range.start,
    socialPoints.data,
    windowSize.height,
    windowSize.width,
    setTooltipData,
  ]);

  return (
    <div className="relative w-full rounded-md border border-stroke-line bg-surface-background">
      <div className="flex justify-end pr-3 pt-3">
        <DateSlider />
      </div>
      <LineChartContainer ref={chartRef} />
    </div>
  );
}

const LineChartContainer = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(function LineChartContainer(props, ref) {
  const showTooltip = useTooltipData((state) =>
    Boolean(state.value?.current.value),
  );
  const currentValue = useTooltipData(
    (state) => state.value?.current.value ?? 0,
  );

  const placement = currentValue < 50 ? "top" : "bottom";

  return (
    <LineChartTooltip
      visible={showTooltip}
      content={<LineChartTooltipContent />}
      allowedPlacements={[placement]}
    >
      <div ref={ref} className="flex h-[9.375rem] w-full" />
    </LineChartTooltip>
  );
});

interface TooltipType {
  visible?: boolean; // Persistent visibility
  content: React.ReactNode;
  allowedPlacements?: Placement[];
  children: React.ReactNode;
}

const LineChartTooltip: React.FC<TooltipType> = ({
  allowedPlacements,
  children,
  content,
  visible,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const arrowRef = useRef(null);

  const middleware = [
    offset(24),
    arrow({
      element: arrowRef,
    }),
    ...(allowedPlacements ? [autoPlacement({ allowedPlacements })] : []),
    ...(arrowRef.current ? [slidingArrow(arrowRef.current)] : []),
  ];

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware,
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useHover(context, {
      enabled: true,
      delay: { open: 0, close: 0 },
    }),
    useClientPoint(context, { enabled: true }),
    useDismiss(context, {
      enabled: false,
      escapeKey: true,
      referencePress: true,
      outsidePress: true,
    }),
  ]);

  const showTooltip = (isOpen && visible !== false) || visible === true;

  return (
    <>
      <div className="flex-1" ref={refs.setReference} {...getReferenceProps()}>
        {children}
      </div>
      {showTooltip && (
        <div
          className={cn(
            "absolute z-10 flex w-max flex-col rounded border border-stroke-line bg-surface-background px-3 py-2 text-sm text-primary-foreground dark:shadow",
          )}
          ref={refs.setFloating}
          style={floatingStyles}
          {...getFloatingProps()}
        >
          {content}
          <FloatingArrow
            style={{
              zIndex: 11,
              clipPath: "polygon(1px 1px, 1px 16px, 16px 16px, 16px 1px)",
              transform: "scale(1.2)",
            }}
            stroke="rgb(var(--stroke-line))"
            fill="rgb(var(--surface-background))"
            strokeWidth={1}
            ref={arrowRef}
            context={context}
          />
        </div>
      )}
    </>
  );
};

const slidingArrow = (arrowEl: HTMLDivElement) => {
  // Keeps tooltip from exiting bounds of container
  return {
    name: "slidingArrow",
    fn({
      elements,
      middlewareData,
      x: cursorX,
    }: {
      elements: any;
      middlewareData: any;
      x: number;
    }) {
      const GAP = 2;
      const ARROW_PADDING = 16;

      const containerEl = elements.reference.contextElement;
      const tooltipEl = elements.floating;
      const arrowPos = middlewareData.arrow.x;
      let xToShiftBy = 0;

      const containerRect = containerEl?.getBoundingClientRect();
      const tooltipRect = tooltipEl?.getBoundingClientRect();

      if (!containerRect || !tooltipRect) {
        return {
          x: cursorX,
        };
      }

      const containerWidth = containerRect.width;
      const tooltipWidth = tooltipRect.width;

      if (cursorX < 0 - GAP) {
        // Left bound
        xToShiftBy = Math.abs(cursorX + GAP);
      } else if (cursorX >= containerWidth - tooltipWidth + GAP) {
        // Right bound
        xToShiftBy = -(cursorX - (containerWidth - tooltipWidth + GAP));
      }
      if (arrowEl && !Number.isNaN(arrowPos)) {
        // Shift arrow
        const arrowShift = arrowPos - xToShiftBy;

        arrowEl.style.left =
          tooltipWidth - arrowShift <= ARROW_PADDING
            ? tooltipWidth - ARROW_PADDING + "px"
            : arrowShift + "px";
      }
      return {
        x: cursorX + xToShiftBy,
      };
    },
  };
};

function LineChartTooltipContent() {
  const tooltipData = useTooltipData((state) => state.value, shallow);

  const value = tooltipData?.current?.value;
  if (!tooltipData || !value) {
    return null;
  }

  const benchmarkValue = tooltipData?.benchmark?.value;

  return (
    <div className="flex max-h-max flex-col gap-2 rounded">
      <div
        data-testid="graph-tooltip"
        className="flex items-center gap-2 text-secondary-foreground"
      >
        <CalendarIcon />
        <span>
          {formatDate(
            tooltipData.current.date,
            TimeFormats.DATE_SHORT,
            "Etc/UTC",
          )}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <div className="h-[1px] w-4 border-t border-primary-foreground" />
        <span>{Math.floor(value)}% protected</span>
      </div>

      {benchmarkValue && (
        <div className="flex items-center gap-2">
          <div className="h-[1px] w-4 border-t border-dashed border-stroke-input" />
          <span>{Math.floor(benchmarkValue)}% benchmark</span>
        </div>
      )}
    </div>
  );
}
